import { INSTANCE_DIFFICULTY_IDS } from './constants'

// Turns live aggregates into the compact, plain-object reports the UI renders.
//
// Two things happen here and nowhere else:
//  * pet and guardian contributions are merged into their owner (ownership is
//    frequently learned after the first pet hit, so merging is deferred to this
//    point);
//  * detail tables are trimmed to the rows worth showing, with the remainder
//    kept as an explicit "other" row so the totals still add up.

const LIMITS = {
    damageBySpell: 60,
    healBySpell: 60,
    damageByTarget: 30,
    healByTarget: 30,
    takenBySpell: 40,
    takenBySource: 30,
    casts: 60,
    auras: 40,
    utility: 25,
    groupAbilities: 40,
    enemies: 30,
}

function sortByAmount(left, right) {
    return right.amount - left.amount
}

function trimRows(rows, limit, mapper) {
    const sorted = rows.sort(sortByAmount)
    if (sorted.length <= limit) return sorted.map(mapper)

    const visible = sorted.slice(0, limit).map(mapper)
    const rest = sorted.slice(limit)
    const other = rest.reduce((acc, row) => {
        acc.amount += row.amount
        acc.hits += row.hits || 0
        acc.overheal += row.overheal || 0
        return acc
    }, { amount: 0, hits: 0, overheal: 0 })

    visible.push({
        key: 'other',
        spellId: 0,
        name: `Other (${rest.length})`,
        amount: other.amount,
        hits: other.hits,
        overheal: other.overheal,
        isOther: true,
    })
    return visible
}

function spellRow(row) {
    return {
        key: row.key || `spell-${row.spellId}`,
        spellId: row.spellId,
        name: row.name,
        school: row.school,
        amount: row.amount,
        hits: row.hits,
        crits: row.crits,
        overheal: row.overheal,
        absorbed: row.absorbed,
        overkill: row.overkill,
        max: row.max,
        min: row.min,
        sourceName: row.sourceName,
        petName: row.petName || '',
    }
}

function unitRow(row) {
    return {
        key: row.key,
        name: row.name,
        amount: row.amount,
        hits: row.hits,
        extra: row.extra,
    }
}

function countRow(row) {
    return { key: `count-${row.id}`, spellId: row.id, name: row.name, amount: row.count, hits: row.count }
}

function mergeMapInto(target, source, petName) {
    source.forEach((row, id) => {
        const key = petName ? `p:${petName}:${id}` : `s:${id}`
        const existing = target.get(key)
        if (!existing) {
            target.set(key, {
                ...row,
                key,
                name: petName ? `${row.name} (${petName})` : row.name,
                petName: petName || '',
            })
            return
        }
        existing.amount += row.amount
        existing.hits += row.hits || 0
        existing.crits = (existing.crits || 0) + (row.crits || 0)
        existing.overheal = (existing.overheal || 0) + (row.overheal || 0)
        existing.absorbed = (existing.absorbed || 0) + (row.absorbed || 0)
        existing.overkill = (existing.overkill || 0) + (row.overkill || 0)
        if ((row.max || 0) > (existing.max || 0)) existing.max = row.max
    })
}

function mergeUnitMapInto(target, source) {
    source.forEach((row, id) => {
        const existing = target.get(id)
        if (!existing) {
            target.set(id, { ...row })
            return
        }
        existing.amount += row.amount
        existing.hits += row.hits || 0
        existing.extra = (existing.extra || 0) + (row.extra || 0)
    })
}

function mergeCountMapInto(target, source) {
    source.forEach((row, id) => {
        const existing = target.get(id)
        if (!existing) {
            target.set(id, { ...row })
            return
        }
        existing.count += row.count
    })
}

function addSeries(target, source) {
    if (!source) return target
    for (let index = 0; index < source.length; index += 1) {
        target[index] = (target[index] || 0) + (source[index] || 0)
    }
    return target
}

function emptyBucket() {
    return {
        damage: 0,
        damageHits: 0,
        damageCrits: 0,
        overkill: 0,
        healing: 0,
        overhealing: 0,
        healingHits: 0,
        healingCrits: 0,
        healingAbsorbed: 0,
        healingReceived: 0,
        absorbDone: 0,
        damageTaken: 0,
        damageTakenAbsorbed: 0,
        damageTakenBlocked: 0,
        damageTakenHits: 0,
        casts: 0,
        interrupts: 0,
        dispels: 0,
        resurrects: 0,
        deaths: 0,
        killingBlows: 0,
        misses: 0,
        activeSeconds: 0,
        firstTs: 0,
        lastTs: 0,
    }
}

const SUMMABLE = [
    'damage', 'damageHits', 'damageCrits', 'overkill',
    'healing', 'overhealing', 'healingHits', 'healingCrits', 'healingAbsorbed', 'healingReceived',
    'absorbDone', 'damageTaken', 'damageTakenAbsorbed', 'damageTakenBlocked', 'damageTakenHits',
    'casts', 'interrupts', 'dispels', 'resurrects', 'deaths', 'killingBlows', 'misses',
]

function perSecond(amount, durationMs) {
    if (!durationMs || durationMs <= 0) return 0
    return (amount * 1000) / durationMs
}

export function buildSegmentReport(segment, registry, context = {}) {
    const endTs = segment.endTs || segment.lastTs || segment.startTs
    const durationMs = Math.max(0, endTs - segment.startTs)
    const activeMs = segment.activeSeconds * 1000
    const fightMs = segment.meta && segment.meta.fightTimeMs ? segment.meta.fightTimeMs : 0
    const basisMs = fightMs || durationMs

    // guid -> merged bucket + detail maps, keyed by the owning player.
    const merged = new Map()

    segment.actors.forEach((stats, guid) => {
        const actor = registry.get(guid)
        const ownerGuid = registry.resolveOwner(guid)
        const owner = registry.get(ownerGuid)
        const isPetContribution = ownerGuid !== guid && owner && owner.isPlayer
        const targetGuid = isPetContribution ? ownerGuid : guid
        const petName = isPetContribution ? (actor ? actor.name : 'Pet') : ''

        let entry = merged.get(targetGuid)
        if (!entry) {
            entry = {
                guid: targetGuid,
                bucket: emptyBucket(),
                damageBySpell: new Map(),
                healBySpell: new Map(),
                damageByTarget: new Map(),
                healByTarget: new Map(),
                takenBySpell: new Map(),
                takenBySource: new Map(),
                castsBySpell: new Map(),
                interruptedSpells: new Map(),
                dispelledSpells: new Map(),
                auraUptime: new Map(),
                missTypes: new Map(),
                pets: [],
                series: { damage: [], healing: [], taken: [] },
            }
            merged.set(targetGuid, entry)
        }

        SUMMABLE.forEach((field) => {
            entry.bucket[field] += stats[field] || 0
        })
        if (!isPetContribution) {
            entry.bucket.activeSeconds = stats.activeSeconds
            entry.bucket.firstTs = stats.firstTs
            entry.bucket.lastTs = stats.lastTs
        } else if (actor && (stats.damage > 0 || stats.healing > 0)) {
            entry.pets.push({ guid, name: actor.name, damage: stats.damage, healing: stats.healing })
        }

        mergeMapInto(entry.damageBySpell, stats.damageBySpell, petName)
        mergeMapInto(entry.healBySpell, stats.healBySpell, petName)
        mergeMapInto(entry.takenBySpell, stats.takenBySpell, '')
        mergeUnitMapInto(entry.damageByTarget, stats.damageByTarget)
        mergeUnitMapInto(entry.healByTarget, stats.healByTarget)
        mergeUnitMapInto(entry.takenBySource, stats.takenBySource)
        mergeCountMapInto(entry.castsBySpell, stats.castsBySpell)
        mergeCountMapInto(entry.interruptedSpells, stats.interruptedSpells)
        mergeCountMapInto(entry.dispelledSpells, stats.dispelledSpells)
        mergeCountMapInto(entry.missTypes, stats.missTypes)
        if (!isPetContribution) mergeCountMapInto(entry.auraUptime, stats.auraUptime)

        const series = segment.timeline.series.get(guid)
        if (series) {
            addSeries(entry.series.damage, series.damage)
            addSeries(entry.series.healing, series.healing)
            addSeries(entry.series.taken, series.taken)
        }
    })

    const groupDamage = [...merged.values()]
        .filter((entry) => {
            const actor = registry.get(entry.guid)
            return actor && actor.isPlayer
        })
        .reduce((sum, entry) => sum + entry.bucket.damage, 0)
    const groupHealing = [...merged.values()]
        .filter((entry) => {
            const actor = registry.get(entry.guid)
            return actor && actor.isPlayer
        })
        .reduce((sum, entry) => sum + entry.bucket.healing, 0)

    const players = []
    const others = []
    const details = {}
    const timelineSeries = {}
    // The live timeline also counts enemy output, which is what the per actor
    // series are for. The group curves shown in the UI are summed here from the
    // same rows the tables show, so chart and table can never disagree.
    const groupSeries = { damage: [], healing: [], taken: [] }

    merged.forEach((entry) => {
        const actor = registry.get(entry.guid)
        if (!actor) return

        const bucket = entry.bucket
        const hasActivity = bucket.damage > 0 || bucket.healing > 0 || bucket.damageTaken > 0 || bucket.casts > 0
        if (!actor.isPlayer || !hasActivity) return

        const playerActiveMs = bucket.activeSeconds * 1000
        const row = {
            guid: entry.guid,
            name: actor.shortName || actor.name,
            fullName: actor.name,
            realm: actor.realm,
            region: actor.region,
            isGroupMember: actor.isGroupMember,
            isLogOwner: actor.isLogOwner,
            specId: actor.specId,
            className: actor.className,
            spec: actor.spec,
            role: actor.role,
            color: actor.color,
            itemLevel: actor.itemLevel,
            maxHp: actor.maxHp,

            damage: bucket.damage,
            dps: perSecond(bucket.damage, basisMs),
            activeDps: perSecond(bucket.damage, playerActiveMs),
            damageShare: groupDamage ? bucket.damage / groupDamage : 0,
            damageHits: bucket.damageHits,
            critRate: bucket.damageHits ? bucket.damageCrits / bucket.damageHits : 0,
            overkill: bucket.overkill,

            healing: bucket.healing,
            hps: perSecond(bucket.healing, basisMs),
            activeHps: perSecond(bucket.healing, playerActiveMs),
            healingShare: groupHealing ? bucket.healing / groupHealing : 0,
            overhealing: bucket.overhealing,
            overhealRate: (bucket.healing + bucket.overhealing)
                ? bucket.overhealing / (bucket.healing + bucket.overhealing)
                : 0,
            healingAbsorbed: bucket.healingAbsorbed,
            healingReceived: bucket.healingReceived,
            absorbDone: bucket.absorbDone,

            damageTaken: bucket.damageTaken,
            dtps: perSecond(bucket.damageTaken, basisMs),
            damageTakenAbsorbed: bucket.damageTakenAbsorbed,
            damageTakenBlocked: bucket.damageTakenBlocked,

            casts: bucket.casts,
            interrupts: bucket.interrupts,
            dispels: bucket.dispels,
            resurrects: bucket.resurrects,
            deaths: bucket.deaths,
            killingBlows: bucket.killingBlows,
            activeMs: playerActiveMs,
            firstTs: bucket.firstTs,
            lastTs: bucket.lastTs,
            // Army style cooldowns create hundreds of short lived guardians, so
            // only the heaviest hitters are listed by name.
            petCount: entry.pets.length,
            pets: entry.pets.sort((left, right) => right.damage - left.damage).slice(0, 12),
        }

        if (actor.isGroupMember) {
            players.push(row)
            addSeries(groupSeries.damage, entry.series.damage)
            addSeries(groupSeries.healing, entry.series.healing)
            addSeries(groupSeries.taken, entry.series.taken)
        } else {
            others.push(row)
        }

        details[entry.guid] = {
            damageBySpell: trimRows([...entry.damageBySpell.values()], LIMITS.damageBySpell, spellRow),
            healBySpell: trimRows([...entry.healBySpell.values()], LIMITS.healBySpell, spellRow),
            damageByTarget: trimRows([...entry.damageByTarget.values()], LIMITS.damageByTarget, unitRow),
            healByTarget: trimRows([...entry.healByTarget.values()], LIMITS.healByTarget, unitRow),
            takenBySpell: trimRows([...entry.takenBySpell.values()], LIMITS.takenBySpell, spellRow),
            takenBySource: trimRows([...entry.takenBySource.values()], LIMITS.takenBySource, unitRow),
            casts: trimRows([...entry.castsBySpell.values()].map((row) => ({ ...row, amount: row.count })), LIMITS.casts, countRow),
            interrupted: trimRows([...entry.interruptedSpells.values()].map((row) => ({ ...row, amount: row.count })), LIMITS.utility, countRow),
            dispelled: trimRows([...entry.dispelledSpells.values()].map((row) => ({ ...row, amount: row.count })), LIMITS.utility, countRow),
            missTypes: [...entry.missTypes.values()].map((row) => ({ name: row.name, amount: row.count })).sort(sortByAmount),
            auras: [...entry.auraUptime.values()]
                .map((row) => ({
                    spellId: row.spellId,
                    name: row.name,
                    auraType: row.auraType,
                    applications: row.applications,
                    uptimeMs: row.totalMs,
                    uptime: durationMs ? Math.min(1, row.totalMs / durationMs) : 0,
                    maxStacks: row.maxStacks,
                    amount: row.totalMs,
                }))
                .sort(sortByAmount)
                .slice(0, LIMITS.auras),
        }

        timelineSeries[entry.guid] = entry.series
    })

    players.sort((left, right) => right.damage - left.damage)
    others.sort((left, right) => right.damage - left.damage)

    // Group wide ability and enemy distributions.
    const groupDamageSpells = new Map()
    const groupHealSpells = new Map()
    const groupEnemies = new Map()
    const groupTakenSources = new Map()

    merged.forEach((entry) => {
        const actor = registry.get(entry.guid)
        if (!actor || !actor.isPlayer || !actor.isGroupMember) return
        mergeMapInto(groupDamageSpells, entry.damageBySpell, '')
        mergeMapInto(groupHealSpells, entry.healBySpell, '')
        mergeUnitMapInto(groupEnemies, entry.damageByTarget)
        mergeUnitMapInto(groupTakenSources, entry.takenBySource)
    })

    const deaths = (segment.deaths || [])
        .map((index) => (context.deaths ? context.deaths[index] : null))
        .filter(Boolean)
        .map((death) => ({
            guid: death.guid,
            name: death.name,
            ts: death.ts,
            offsetMs: death.ts - segment.startTs,
            encounterName: death.encounterName,
            hp: death.hp,
            maxHp: death.maxHp,
            killingBlow: death.killingBlow,
            events: death.events,
            activeAuras: death.activeAuras,
            windowMs: death.windowMs,
        }))

    const roster = [...segment.roster.values()].map((entry) => {
        const actor = registry.get(entry.guid)
        return {
            guid: entry.guid,
            name: actor ? actor.shortName || actor.name : entry.guid,
            realm: actor ? actor.realm : '',
            region: actor ? actor.region : '',
            specId: entry.specId,
            className: entry.className,
            spec: entry.spec,
            role: entry.role,
            itemLevel: entry.itemLevel,
            color: actor ? actor.color : '',
        }
    })

    return {
        key: segment.key,
        type: segment.type,
        name: segment.name,
        meta: { ...segment.meta },
        startTs: segment.startTs,
        endTs,
        durationMs,
        activeMs,
        fightMs,
        basisMs,
        basis: fightMs ? 'encounter' : 'segment',
        eventCount: segment.eventCount,
        enemyDeaths: segment.enemyDeaths,
        totals: {
            ...segment.totals,
            damage: groupDamage,
            healing: groupHealing,
            dps: perSecond(groupDamage, basisMs),
            hps: perSecond(groupHealing, basisMs),
        },
        players,
        others,
        details,
        roster,
        deaths,
        abilities: {
            damage: trimRows([...groupDamageSpells.values()], LIMITS.groupAbilities, spellRow),
            healing: trimRows([...groupHealSpells.values()], LIMITS.groupAbilities, spellRow),
        },
        enemies: trimRows([...groupEnemies.values()], LIMITS.enemies, unitRow),
        damageTakenSources: trimRows([...groupTakenSources.values()], LIMITS.takenBySource, unitRow),
        timeline: {
            startTs: segment.timeline.startTs,
            bucketMs: segment.timeline.bucketMs,
            length: segment.timeline.length,
            total: groupSeries,
            allActors: {
                damage: segment.timeline.totalDamage,
                healing: segment.timeline.totalHealing,
                taken: segment.timeline.totalTaken,
            },
            series: timelineSeries,
        },
    }
}

// A log usually starts and ends in a city, so the zone worth naming is the one
// that saw the most events, preferring instances over the open world.
function pickPrimaryZone(zones) {
    if (!zones || !zones.size) return null
    const all = [...zones.values()]
    const instances = all.filter((zone) => INSTANCE_DIFFICULTY_IDS.has(zone.difficultyId))
    const pool = instances.length ? instances : all
    return pool.reduce((best, zone) => (!best || zone.events > best.events ? zone : best), null)
}

// The file level summary shown in the log list and the overview header.
export function buildFileReport(analyzer) {
    const { state, registry } = analyzer
    analyzer.sealForReport()

    const segments = []
    const allSegments = [...analyzer.finishedSegments, ...analyzer.openSegments]

    allSegments.forEach((segment) => {
        segments.push(buildSegmentReport(segment, registry, { deaths: state.deaths }))
    })

    const logReport = buildSegmentReport(analyzer.logSegment, registry, { deaths: state.deaths })
    logReport.name = 'Whole log'

    segments.sort((left, right) => left.startTs - right.startTs)

    const challengeRuns = segments.filter((segment) => segment.type === 'challenge')
    const encounters = segments.filter((segment) => segment.type === 'encounter')

    return {
        fileName: state.fileName,
        logVersion: state.logVersion,
        buildVersion: state.buildVersion,
        advancedLogging: state.advancedLogging,
        dateOrder: state.dateOrder,
        firstTs: state.firstTs,
        lastTs: state.lastTs,
        durationMs: Math.max(0, state.lastTs - state.firstTs),
        lineCount: state.lineCount,
        eventCount: state.eventCount,
        skippedLines: state.skippedLines,
        parseErrors: state.parseErrors,
        offset: state.offset,
        size: state.size,
        zone: state.zone ? { ...state.zone } : null,
        primaryZone: pickPrimaryZone(state.zones),
        zones: state.zones ? [...state.zones.values()].sort((left, right) => right.events - left.events).slice(0, 12) : [],
        deathCount: state.deathCount,
        enemyDeaths: state.enemyDeaths,
        unknownEvents: [...state.unknownEvents.entries()].map(([name, count]) => ({ name, count })),
        logOwner: registry.logOwner
            ? {
                guid: registry.logOwner.guid,
                name: registry.logOwner.shortName,
                realm: registry.logOwner.realm,
                region: registry.logOwner.region,
                className: registry.logOwner.className,
                spec: registry.logOwner.spec,
                specId: registry.logOwner.specId,
            }
            : null,
        segments: [logReport, ...segments],
        challengeRuns: challengeRuns.map((run) => ({
            key: run.key,
            name: run.name,
            startTs: run.startTs,
            endTs: run.endTs,
            durationMs: run.durationMs,
            meta: run.meta,
            deaths: run.deaths.length,
        })),
        encounters: encounters.map((encounter) => ({
            key: encounter.key,
            name: encounter.name,
            startTs: encounter.startTs,
            endTs: encounter.endTs,
            durationMs: encounter.durationMs,
            meta: encounter.meta,
            deaths: encounter.deaths.length,
        })),
    }
}
